import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import DataEntryTab from './components/DataEntryTab';
import DashboardTab from './components/DashboardTab';
import AnalysisTab from './components/AnalysisTab';
import SettingsTab from './components/SettingsTab';
import CustomersTab from './components/CustomersTab';
import Toast from './components/Toast';
import { entriesAPI, creditsAPI, employeeCreditsAPI, customersAPI, employeesAPI, settingsAPI } from './services/api';
import { useToast } from './hooks/useToast';
import { transformEntries, transformCredits, transformEntry, transformCredit } from './utils/dataTransform';

function App() {
  const [activeTab, setActiveTab] = useState('data-entry');
  const [data, setData] = useState([]);
  const [customerCredits, setCustomerCredits] = useState([]);
  const [employeeCredits, setEmployeeCredits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState({
    groups: ['Group A', 'Group B'],
    currency: '€'
  });

  const [loading, setLoading] = useState(false); // Start as false so form shows immediately
  const [error, setError] = useState(null);
  const { toasts, removeToast, success, error: showError } = useToast();

  // Load data from API on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load entries
        const entries = await entriesAPI.getAll();
        console.log('Loaded entries from API:', entries);
        const transformed = transformEntries(entries);
        console.log('Transformed entries:', transformed);
        setData(transformed);

        // Load customer credits
        const credits = await creditsAPI.getAll();
        setCustomerCredits(transformCredits(credits));

        // Load employee credits
        const empCredits = await employeeCreditsAPI.getAll();
        setEmployeeCredits(transformCredits(empCredits));

        // Load customers
        const customersList = await customersAPI.getAll();
        setCustomers(customersList);

        // Load employees
        const employeesList = await employeesAPI.getAll();
        setEmployees(employeesList);

        // Load settings
        const appSettings = await settingsAPI.get();
        setSettings({
          groups: appSettings.groups || ['Group A', 'Group B'],
          currency: appSettings.currency || '€'
        });
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message || 'Failed to load data from server');
        // Show error as warning but don't block UI
        showError('Unable to connect to server. You can still use the form, but data won\'t be saved.');
        // Fallback to default settings
        setSettings({
          groups: ['Group A', 'Group B'],
          currency: '€'
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Refresh data from API
  const refreshData = useCallback(async () => {
    try {
      const entries = await entriesAPI.getAll(false); // Don't use cache for refresh
      setData(transformEntries(entries));

      const credits = await creditsAPI.getAll(false);
      setCustomerCredits(transformCredits(credits));

      const empCredits = await employeeCreditsAPI.getAll(false);
      setEmployeeCredits(transformCredits(empCredits));

      const customersList = await customersAPI.getAll(false);
      setCustomers(customersList);

      const employeesList = await employeesAPI.getAll(false);
      setEmployees(employeesList);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError(err.message || 'Failed to refresh data');
      showError('Failed to refresh data: ' + err.message);
    }
  }, [showError]);

  const addEntry = useCallback(async (entry) => {
    try {
      const savedEntry = await entriesAPI.create(entry);
      setData(prev => [...prev, transformEntry(savedEntry)]);
      success('Entry saved successfully!');
    } catch (err) {
      console.error('Error adding entry:', err);
      showError('Failed to save entry: ' + err.message);
      throw err;
    }
  }, [success, showError]);

  const updateEntry = useCallback(async (id, entry) => {
    try {
      const updatedEntry = await entriesAPI.update(id, entry);
      setData(prev => prev.map(item => 
        (item._id === id || item.id === id) 
          ? transformEntry(updatedEntry)
          : item
      ));
      success('Entry updated successfully!');
    } catch (err) {
      console.error('Error updating entry:', err);
      showError('Failed to update entry: ' + err.message);
      throw err;
    }
  }, [success, showError]);

  const addCustomerCredit = useCallback(async (credit) => {
    try {
      const savedCredit = await creditsAPI.create(credit);
      setCustomerCredits(prev => [...prev, transformCredit(savedCredit)]);
      
      // Refresh data to update total_credit in entries
      await refreshData();
      success('Customer credit saved successfully!');
    } catch (err) {
      console.error('Error adding credit:', err);
      showError('Failed to save credit: ' + err.message);
      throw err;
    }
  }, [refreshData, success, showError]);

  const addEmployeeCredit = useCallback(async (credit) => {
    try {
      const savedCredit = await employeeCreditsAPI.create(credit);
      setEmployeeCredits(prev => [...prev, transformCredit(savedCredit)]);
      
      // Refresh data to update total_credit in entries
      await refreshData();
      success('Employee credit saved successfully!');
    } catch (err) {
      console.error('Error adding employee credit:', err);
      showError('Failed to save employee credit: ' + err.message);
      throw err;
    }
  }, [refreshData, success, showError]);

  const updateSettings = useCallback(async (newSettings) => {
    try {
      const updated = await settingsAPI.update(newSettings);
      setSettings({
        groups: updated.groups || newSettings.groups,
        currency: updated.currency || newSettings.currency
      });
      success('Settings updated successfully!');
    } catch (err) {
      console.error('Error updating settings:', err);
      showError('Failed to update settings: ' + err.message);
    }
  }, [success, showError]);

  const clearAllData = useCallback(async (onConfirm) => {
    if (onConfirm) {
      try {
        await Promise.all([
          entriesAPI.deleteAll(),
          creditsAPI.deleteAll(),
          employeeCreditsAPI.deleteAll(),
          customersAPI.deleteAll?.() || Promise.resolve(),
          employeesAPI.deleteAll?.() || Promise.resolve()
        ]);
        setData([]);
        setCustomerCredits([]);
        setEmployeeCredits([]);
        setCustomers([]);
        setEmployees([]);
        success('All data cleared successfully');
      } catch (err) {
        console.error('Error clearing data:', err);
        showError('Failed to clear data: ' + err.message);
      }
    }
  }, [success, showError]);

  const deleteEntry = useCallback(async (id) => {
    try {
      await entriesAPI.delete(id);
      setData(prev => prev.filter(entry => entry._id !== id && entry.id !== id));
      success('Entry deleted successfully');
    } catch (err) {
      console.error('Error deleting entry:', err);
      showError('Failed to delete entry: ' + err.message);
    }
  }, [success, showError]);

  return (
    <div className="App">
      <div className="app-container">
        <header className="app-header">
          <h1>Sofra Revenue Calculator</h1>
        </header>
        
        <nav className="tab-navigation">
          <button 
            className={activeTab === 'data-entry' ? 'active' : ''}
            onClick={() => setActiveTab('data-entry')}
          >
            Data Entry
          </button>
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={activeTab === 'analysis' ? 'active' : ''}
            onClick={() => setActiveTab('analysis')}
          >
            Analysis
          </button>
          <button 
            className={activeTab === 'customers' ? 'active' : ''}
            onClick={() => setActiveTab('customers')}
          >
            Customers
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </nav>

        <main className="tab-content">
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading data...</p>
            </div>
          )}
          {error && !loading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '15px', 
              marginBottom: '20px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              color: '#856404'
            }}>
              <p><strong>Warning:</strong> {error}</p>
              <button 
                onClick={() => window.location.reload()}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#ffc107',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry Connection
              </button>
            </div>
          )}
          {!loading && (
            <>
              {activeTab === 'data-entry' && (
                <DataEntryTab 
                  onAddEntry={addEntry}
                  onUpdateEntry={updateEntry}
                  onAddCustomerCredit={addCustomerCredit}
                  onAddEmployeeCredit={addEmployeeCredit}
                  customerCredits={customerCredits}
                  employeeCredits={employeeCredits}
                  customers={customers}
                  employees={employees}
                  entries={data}
                  onRefreshCustomers={refreshData}
                  settings={settings}
                  toast={{ success, error: showError }}
                />
              )}
              {activeTab === 'dashboard' && (
                <DashboardTab 
                  data={data} 
                  settings={settings}
                  onDeleteEntry={deleteEntry}
                />
              )}
              {activeTab === 'analysis' && (
                <AnalysisTab 
                  data={data} 
                  settings={settings}
                />
              )}
              {activeTab === 'customers' && (
                <CustomersTab 
                  settings={settings}
                />
              )}
              {activeTab === 'settings' && (
                <SettingsTab 
                  settings={settings}
                  onUpdateSettings={updateSettings}
                  onClearData={clearAllData}
                  data={data}
                />
              )}
            </>
          )}
        </main>
      </div>
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
            duration={toast.duration}
          />
        ))}
      </div>
    </div>
  );
}

export default App;

